import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import styles from './landing.module.css';

import clsxm from '@/lib/clsxm';

type Props = {
  text: string;
  className?: string;
  size?: 'md' | 'lg';
  /**
   * The text was written by visitors or the model (examples, results): links
   * get `nofollow ugc` so search engines don't treat them as our endorsement.
   */
  userContent?: boolean;
};

/**
 * Markdown for landing-page text: examples, results previews and FAQ answers.
 *
 * Deliberately not the chat's MarkdownBlock, which brings KaTeX, Mermaid and a
 * syntax highlighter (over 1 MB of JavaScript) that plain names, lists and
 * letters never need. Raw HTML is not rendered (react-markdown's default).
 */
export default function LightMarkdown({
  text,
  className,
  size = 'md',
  userContent = false,
}: Props) {
  return (
    <div
      className={clsxm(
        styles.prose,
        size === 'lg' && styles.proseLg,
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }: { href?: string; children?: ReactNode }) => {
            const external = !!href && /^https?:\/\//i.test(href);
            return (
              <a
                href={href}
                target={external ? '_blank' : undefined}
                rel={
                  external
                    ? userContent
                      ? 'nofollow ugc noopener noreferrer'
                      : 'noopener noreferrer'
                    : undefined
                }
              >
                {children}
              </a>
            );
          },
          img: ({ src, alt }: { src?: unknown; alt?: string }) =>
            typeof src === 'string' && src ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={src} alt={alt || ''} loading='lazy' decoding='async' />
            ) : null,
          table: ({ children }: { children?: ReactNode }) => (
            <div className={styles.tableWrap}>
              <table>{children}</table>
            </div>
          ),
        }}
      >
        {/* Single newlines are line breaks: generated lists of names or
            lines of a poem often come one per line, without blank lines. */}
        {text.replace(/\n/g, '  \n').trim()}
      </ReactMarkdown>
    </div>
  );
}
